/**
 * ULID: 26 tekens Crockford base32, sorteerbaar op tijd.
 *
 * De eerste 10 tekens zijn de tijd in milliseconden (48 bits), de laatste 16
 * zijn 80 bits willekeur uit `crypto.getRandomValues`. De API controleert een
 * apparaat-id met Laravels `ulid`-regel: precies deze vorm, en het eerste teken
 * hoogstens een 7.
 */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const TIME_LENGTH = 10;
const RANDOM_BYTES = 10;
const MAX_TIME = 2 ** 48 - 1;

export const ULID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

export function isUlid(value: unknown): value is string {
    return typeof value === 'string' && ULID_PATTERN.test(value);
}

/** De tijd als 10 tekens base32. */
export function encodeTime(time: number): string {
    if (!Number.isInteger(time) || time < 0 || time > MAX_TIME) {
        throw new RangeError(`Een ULID-tijd ligt tussen 0 en ${MAX_TIME}, niet ${time}.`);
    }

    let rest = time;
    let encoded = '';

    for (let index = 0; index < TIME_LENGTH; index += 1) {
        encoded = ALPHABET.charAt(rest % 32) + encoded;
        rest = Math.floor(rest / 32);
    }

    return encoded;
}

/** 10 bytes (80 bits) als 16 tekens base32, hoogste bit eerst. */
export function encodeRandom(bytes: Uint8Array): string {
    if (bytes.length !== RANDOM_BYTES) {
        throw new RangeError(`Het willekeurige deel van een ULID is ${RANDOM_BYTES} bytes.`);
    }

    let encoded = '';
    let buffer = 0;
    let bits = 0;

    for (const byte of bytes) {
        buffer = (buffer << 8) | byte;
        bits += 8;

        while (bits >= 5) {
            bits -= 5;
            encoded += ALPHABET.charAt((buffer >> bits) & 31);
        }

        buffer &= (1 << bits) - 1;
    }

    return encoded;
}

export function randomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length));
}

export function ulid(now: number = Date.now()): string {
    return encodeTime(now) + encodeRandom(randomBytes(RANDOM_BYTES));
}
